'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './page.module.css';

/* ── helper: join class names conditionally ── */
const cx = (...cls: (string | false | undefined)[]) => cls.filter(Boolean).join(' ');

/* ── Static data ── */
const integrations = [
  { initials: 'Ep', name: 'Epic',               bg: 'linear-gradient(135deg,#7f1d1d,#ef4444)' },
  { initials: 'De', name: 'Dentrix',            bg: 'linear-gradient(135deg,#1e3a8a,#60a5fa)' },
  { initials: 'DA', name: 'Dentrix Ascend',     bg: 'linear-gradient(135deg,#075985,#38bdf8)' },
  { initials: 'DE', name: 'Dentrix Enterprise', bg: 'linear-gradient(135deg,#0f172a,#3b82f6)' },
  { initials: 'OD', name: 'Open Dental',        bg: 'linear-gradient(135deg,#14532d,#4ade80)' },
  { initials: 'Ea', name: 'Eaglesoft',          bg: 'linear-gradient(135deg,#7c2d12,#fb923c)' },
  { initials: 'Cu', name: 'Curve Dental',       bg: 'linear-gradient(135deg,#4c1d95,#c084fc)' },
  { initials: 'Di', name: 'Denticon',           bg: 'linear-gradient(135deg,#312e81,#818cf8)' },
  { initials: 'CS', name: 'CS OrthoTrac',       bg: 'linear-gradient(135deg,#1e293b,#94a3b8)' },
  { initials: 'Do', name: 'Dolphin',            bg: 'linear-gradient(135deg,#0c4a6e,#7dd3fc)' },
  { initials: 'PW', name: 'PracticeWorks',      bg: 'linear-gradient(135deg,#44403c,#d6d3d1)' },
  { initials: 'SD', name: 'SoftDent',           bg: 'linear-gradient(135deg,#0f172a,#64748b)' },
  { initials: 'iD', name: 'iDentalSoft',        bg: 'linear-gradient(135deg,#831843,#f472b6)' },
  { initials: 'C9', name: 'Cloud9 Ortho',       bg: 'linear-gradient(135deg,#164e63,#22d3ee)' },
  { initials: 'TD', name: 'TDO Software',       bg: 'linear-gradient(135deg,#4c0519,#fb7185)' },
];

const featureCards = [
  { cls: styles.fc1, img: '/images/24x7a.png',     title: '24/7 Automated Answering', desc: 'Never miss a call, even during peak hours or after business hours' },
  { cls: styles.fc2, img: '/images/dashboard.png', title: 'Analytics Dashboard',      desc: 'Track call volume, popular questions, and customer insights' },
  { cls: styles.fc3, img: '/images/speed.png',     title: 'Setup in Minutes',         desc: 'Upload documents and go live in under 5 minutes' },
  { cls: styles.fc4, img: '/images/HIPAA.png',     title: 'HIPAA Compliant',          desc: 'Enterprise-grade security for healthcare data' },
  { cls: styles.fc5, img: '/images/multiLang.png', title: 'Multi-Language Support',   desc: 'Serve customers in their preferred language' },
  { cls: styles.fc6, img: '/images/ipad.png',      title: 'Easy Updates',             desc: 'Change menu, hours, or services anytime from dashboard' },
];

const faqItems = [
  {
    q: 'What is an AI voice agent?',
    a: 'An AI voice agent is an autonomous system that can listen, speak, and hold natural phone conversations like a human. It uses speech recognition, large language models, and text-to-speech technology to understand callers, respond intelligently, maintain context, and take actions such as booking appointments or updating systems without relying on rigid scripts or human intervention.',
  },
  {
    q: 'How does it work?',
    a: '4 simple steps, under 5 minutes:\n\n• Build your company profile\n• Build your agent – upload a document, paste your website link, or fill out a simple template\n• Claim your phone number\n• Go live 24/7 – no hold times, no missed calls',
  },
  {
    q: "What if the AI doesn't understand the caller?",
    a: "Our AI is designed to handle a wide variety of questions. If it can't resolve an inquiry, it will take a message and notify you, so no customer is ever left without a response.",
  },
  {
    q: 'Can I customize the voice?',
    a: 'Yes! You can choose from multiple voice options and customize the tone, name, and personality of your AI agent from the dashboard.',
  },
  {
    q: 'How secure is my data?',
    a: 'We take security seriously. All data is encrypted at rest and in transit. Our platform is HIPAA compliant, making it suitable for healthcare businesses.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. There are no long-term contracts. You can cancel or downgrade your plan at any time from your account dashboard.',
  },
  {
    q: 'Do I get a phone number with my plan, and can I keep my existing number?',
    a: 'Yes — every plan includes a dedicated phone number linked to your AI voice agent. You have two options:\n\n• Use your new number directly — the simplest setup. Share this number with your customers and your AI agent handles all calls automatically. No extra configuration needed.\n\n• Keep your existing number — forward your current business number to your new AI agent number. This is entirely your choice and your responsibility to set up with your carrier.',
    link: { text: 'Phone Forwarding Guide', href: '/docs#phone-number' },
  },
];

const WaveIcon = () => (
  <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="1.5"  cy="3"   r="1.5" fill="currentColor"/>
    <circle cx="4.5"  cy="6.5" r="1.5" fill="currentColor"/>
    <circle cx="7"    cy="8.5" r="1.5" fill="currentColor"/>
    <circle cx="9.5"  cy="6.5" r="1.5" fill="currentColor"/>
    <circle cx="12.5" cy="3"   r="1.5" fill="currentColor"/>
  </svg>
);

const TOTAL_CARDS = 6;

/* ════════════════════════════════════════════
   HomePage Component
   ════════════════════════════════════════════ */
export default function HomePage() {
  /* ── UI State ── */
  const [openFaq,    setOpenFaq]    = useState<number | null>(null);
  const [intPaused,  setIntPaused]  = useState(false);
  const [currentDot, setCurrentDot] = useState(0);
  const [btnPaused,  setBtnPaused]  = useState(false);

  /* ── Carousel Refs ── */
  const featTrackRef = useRef<HTMLDivElement>(null);
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const hoverPaused  = useRef(false);

  /* Read actual card width from DOM (card width + gap) */
  const getCardWidth = () => {
    if (!featTrackRef.current) return 412;
    const card = featTrackRef.current.querySelector('div') as HTMLElement | null;
    if (!card) return 412;
    return card.offsetWidth + 12; // 12px = gap
  };

  /* Move carousel to a specific slide */
  const goTo = (idx: number) => {
    const next = ((idx % TOTAL_CARDS) + TOTAL_CARDS) % TOTAL_CARDS;
    setCurrentDot(next);
    if (featTrackRef.current) {
      featTrackRef.current.style.transform = `translateX(-${next * getCardWidth()}px)`;
    }
  };

  /* Start/restart the auto-advance interval */
  const startInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (hoverPaused.current) return;
      setCurrentDot(prev => {
        const next = (prev + 1) % TOTAL_CARDS;
        if (featTrackRef.current) {
          featTrackRef.current.style.transform = `translateX(-${next * getCardWidth()}px)`;
        }
        return next;
      });
    }, 3000);
  };

  /* Start interval on mount; stop when button-paused */
  useEffect(() => {
    if (!btnPaused) {
      startInterval();
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [btnPaused]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFaq = (idx: number) => setOpenFaq(openFaq === idx ? null : idx);

  /* ════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════ */
  return (
    <div className={styles.pageWrapper}>
      <div className={styles.stars} />

      {/* ── NAV ── */}
      <NavBar />

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroTextLayer}>
          <div className={styles.badge}>
            <span className={styles.badgeDot} />
            AI Voice Agent · Always Answering
          </div>

          <h1 className={styles.heroH1}>
            Every Call Answered. <em>Every Caller Respected.</em>
          </h1>

          <p className={styles.heroSub}>
            NovaMind <strong className={styles.heroSubStrong}>AI</strong> voice agent handles every call with the same care and professionalism — 24/7, no bias, no missed opportunities, no lost customers.{' '}
            <strong className={styles.heroSubStrong}>Your business value grows with every call.</strong>
          </p>

          <div className={styles.heroCta}>
            <div className={styles.demoCards}>
              <a href="tel:5599109756" className={styles.demoCard}>
                <svg className={styles.demoCardSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <div className={styles.demoCardText}>
                  <div className={styles.demoCardLabel}>Try a Live Dental Demo</div>
                  <div className={styles.demoCardValue}>559-910-9756</div>
                </div>
              </a>
              <a href="tel:6183481765" className={styles.demoCard}>
                <svg className={styles.demoCardSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <div className={styles.demoCardText}>
                  <div className={styles.demoCardLabel}>Live Restaurant Demo</div>
                  <div className={styles.demoCardValue}>618-348-1765</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Video Background */}
        <div className={styles.heroVideoBg}>
          <video autoPlay muted loop playsInline poster="/images/poster.png">
            <source src="/videos/Voice_video.mp4" type="video/mp4" />
          </video>
          <div className={styles.heroVideoOverlay} />
        </div>

        <div className={styles.heroFog} />

        {/* Integrations label */}
        <div className={styles.heroIntLabel}>
          <h2 className={styles.heroIntTitle}>
            Supported Integrations for Our AI Voice Agent Platform
          </h2>
          <p className={styles.heroIntSub}>
            Seamlessly connect with leading EHR and dental practice management systems.
          </p>
        </div>
      </section>

      {/* ── INTEGRATIONS CAROUSEL ── */}
      <section id="integrations" className={styles.intSection}>
        <div className={styles.carouselTrackWrap}>
          <div className={cx(styles.carouselTrack, intPaused && styles.paused)}>
            {[...integrations, ...integrations].map((item, i) => (
              <div
                key={i}
                className={styles.intCard}
                onMouseEnter={() => setIntPaused(true)}
                onMouseLeave={() => setIntPaused(false)}
              >
                {/* background is data-driven per integration — must stay inline */}
                <div className={styles.intIcon} style={{ background: item.bg }}>
                  {item.initials}
                </div>
                <div className={styles.intName}>{item.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.divider} />

      {/* ── INDUSTRIES ── */}
      <section id="industries" className={styles.industriesSection}>
        <div className={styles.showcaseGrid}>
          <div className={styles.showcaseHeaderRow}>
            <h2>Choose Your Industry</h2>
            <p>Select your business type to get started</p>
          </div>

          {/* Medical */}
          <div className={cx(styles.showcaseTile, styles.tileDark)}>
            <div className={styles.tileLabel}>Healthcare</div>
            <div className={styles.tileTitle}>Medical</div>
            <div className={styles.tileSub}>Patient identity verification, prescription status, appointment inquiries, and insurance coverage — all automated.</div>
            <div className={styles.tileActions}>
              <Link href="/register?industry=medical" className={styles.tileBtnPrimary}>Get Started</Link>
              <Link href="/solutions/medical" className={styles.tileBtnGhost}>Learn more</Link>
            </div>
            <div className={styles.tileVisual}>
              <img src="/images/medical.png" alt="Medical AI Voice Agent" />
            </div>
          </div>

          {/* Dental */}
          <div className={cx(styles.showcaseTile, styles.tileNavy)}>
            <div className={styles.tileLabel}>Healthcare</div>
            <div className={styles.tileTitle}>Dental</div>
            <div className={styles.tileSub}>AI that books appointments, answers patient questions, and handles insurance inquiries — 24/7.</div>
            <div className={styles.tileActions}>
              <Link href="/register?industry=dental" className={styles.tileBtnPrimary}>Get Started</Link>
              <Link href="/solutions/dental" className={styles.tileBtnGhost}>Learn more</Link>
            </div>
            <div className={styles.tileVisual}>
              <img src="/images/dental.png" alt="Dental Clinic AI Voice Agent" />
            </div>
          </div>

          {/* Restaurant */}
          <div className={cx(styles.showcaseTile, styles.tileDeep)}>
            <div className={styles.tileLabel}>Hospitality</div>
            <div className={styles.tileTitle}>Restaurant</div>
            <div className={styles.tileSub}>Take orders, confirm reservations, and answer menu questions automatically — even during rush hours.</div>
            <div className={styles.tileActions}>
              <Link href="/register?industry=restaurant" className={styles.tileBtnPrimary}>Get Started</Link>
              <Link href="/solutions/restaurant" className={styles.tileBtnGhost}>Learn more</Link>
            </div>
            <div className={styles.tileVisual}>
              <img src="/images/restaurant.png" alt="Restaurant AI Voice Agent" />
            </div>
          </div>

          {/* General */}
          <div className={cx(styles.showcaseTile, styles.tileSlate)}>
            <div className={styles.tileLabel}>Any Industry</div>
            <div className={styles.tileTitle}>General Purpose AI Agent</div>
            <div className={styles.tileSub}>{"Can't find your industry? Our AI adapts to any business — customer support, scheduling, FAQs and more."}</div>
            <div className={styles.tileActions}>
              <Link href="/register?industry=general" className={styles.tileBtnPrimary}>Get Started</Link>
              <Link href="/solutions/general" className={styles.tileBtnGhost}>Learn more</Link>
            </div>
            <div className={styles.tileVisual}>
              <img src="/images/call_center.png" alt="General Purpose AI Voice Agent" />
            </div>
          </div>

          {/* Custom Agent Banner */}
          <div className={styles.customBanner}>
            <Image
              src="/images/meeting.jpg"
              alt="Custom Agent background"
              fill
              className={styles.customBannerImage}
            />
            <div className={styles.customBannerOverlay} />
            <div className={styles.tileLabel} style={{ color: 'rgba(255,255,255,0.7)' }}>Custom Solutions</div>
            <div className={styles.customBannerTitle}>Need a Fully Custom Voice Agent?</div>
            <div className={styles.customBannerSub}>For companies with unique workflows and specific requirements — we design and build a voice agent from the ground up, built exactly to your specs.</div>
            <div className={styles.customBannerActions}>
              <Link href="/register?industry=custom" className={styles.tileBtnPrimary}>Request a Custom Agent</Link>
              <Link href="/solutions/custom" className={styles.tileBtnGhost}>Learn more</Link>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionSpacer} />

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksInner}>
          <div className={styles.sectionHeaderNarrow}>
            <h2>How It Works</h2>
            <p>From sign-up to live calls in under 5 minutes</p>
          </div>
        </div>

        <div className={styles.steps}>
          {/* Step 01 */}
          <div className={styles.stepHero}>
            <div className={styles.step}>
              <div className={styles.stepNum}>01</div>
              <h3>Build Your Company Profile</h3>
              <p>Give your agent the knowledge it needs.</p>
              <ul className={cx(styles.stepList, styles.accentBullets)}>
                <li><span>•</span><span>Fill out your business template manually,</span></li>
                <li><span>•</span><span>Upload a document (PDF, Word, or plain text),</span></li>
                <li><span>•</span><span>{"Or simply drop in your website URL — we'll extract everything automatically."}</span></li>
              </ul>
              <div className={styles.step01ImgWrapper}>
                <img src="/images/01.png" alt="" className={styles.step01Img} />
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className={styles.stepsRight}>
            {/* Steps 02 & 03 */}
            <div className={styles.stepsRightTop}>
              <div className={styles.step}>
                <div className={styles.step02Robot}>🤖</div>
                <div className={styles.stepNum}>02</div>
                <h3>Build Your Agent</h3>
                <ul className={cx(styles.stepList, styles.darkBullets)}>
                  <li><span>•</span><span>Name your agent, set its language, customize the greeting, and define max call duration.</span></li>
                  <li><span>•</span><span>Health practices connect their EHR system (NexHealth for dental, Epic for medical).</span></li>
                  <li><span>•</span><span>General businesses are ready to go from Step 1.</span></li>
                </ul>
              </div>

              <div className={cx(styles.step, styles.step03)}>
                <div className={styles.step03Left}>
                  <div className={styles.stepNum}>03</div>
                  <h3>Claim Your Phone Number</h3>
                  <ul className={cx(styles.stepList, styles.darkBullets)}>
                    <li><span>•</span><span>Choose a dedicated local or toll-free number, ready to take real calls immediately.</span></li>
                    <li><span>•</span><span>Optionally, set behavioral rules to define exactly how your agent handles different situations.</span></li>
                  </ul>
                </div>
                <div className={styles.step03ImgCol}>
                  <img src="/images/03.png" alt="" className={styles.step03Img} />
                </div>
              </div>
            </div>

            {/* Step 04 */}
            <div className={styles.stepFinale}>
              <div className={cx(styles.step, styles.step04)}>
                <div className={styles.step04Left}>
                  <div className={styles.stepNum}>04</div>
                  <h3>Go Live 24/7</h3>
                  <p>Your agent is on. Every call answered, day or night —</p>
                  <ul className={cx(styles.stepList, styles.darkBullets, styles.lgText)}>
                    <li><span>•</span><span>No hold times</span></li>
                    <li><span>•</span><span>No missed customers</span></li>
                    <li><span>•</span><span>No extra staff needed</span></li>
                  </ul>
                </div>
                <div className={styles.step04ImgCol}>
                  <img src="/images/04.png" alt="" className={styles.step04Img} />
                  <div className={styles.step04Overlay} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionSpacer} />

      {/* ── FEATURES ── */}
      <section id="features" className={styles.featuresSection}>
        <div className={styles.featuresSectionInner}>
          <div className={styles.sectionHeader}>
            <h2>Key Features</h2>
            <p>Everything you need to automate your phone calls</p>
          </div>
        </div>

        <div className={styles.featuresTrackWrapper}>
          <div ref={featTrackRef} className={styles.featuresTrack}>
            {[...featureCards, ...featureCards].map((card, i) => (
              <div
                key={i}
                className={cx(styles.featureCard, card.cls)}
                /* backgroundImage is data-driven per card — must stay inline */
                style={{ backgroundImage: `url('${card.img}')` }}
                onMouseEnter={() => { hoverPaused.current = true; }}
                onMouseLeave={() => { if (!btnPaused) hoverPaused.current = false; }}
              >
                <div className={styles.featOverlay} />
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.featuresControls}>
          <div className={styles.featuresDots}>
            {featureCards.map((_, i) => (
              <div
                key={i}
                className={cx(styles.featDot, currentDot === i && styles.active)}
                onClick={() => { goTo(i); startInterval(); }}
              />
            ))}
          </div>
          <button
            className={styles.featuresPauseBtn}
            onClick={() => setBtnPaused(p => !p)}
            title={btnPaused ? 'Play' : 'Pause'}
          >
            {btnPaused ? '▶' : '⏸'}
          </button>
        </div>
      </section>

      <div className={styles.sectionSpacer} />

      {/* ── PRICING ── */}
      <section id="pricing" className={styles.pricingSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2>Simple, Transparent Pricing</h2>
            <p>Choose the plan that fits your business</p>
            <p className={styles.trialBadge}>14-Day Free Trial — Start free, cancel anytime</p>
          </div>

          <div className={styles.pricingGrid}>
            {/* Starter */}
            <div className={styles.priceCard}>
              <div className={styles.planTitle}>Starter</div>
              <div className={styles.planDesc}>Automate customer calls 24/7 with no wait time. Up and running in minutes.</div>
              <Link href="/register" className={styles.btnPriceOutline}>Get Started</Link>
              <div className={styles.priceDetailsLabel}>Details</div>
              <ul className={styles.priceDetailsItems}>
                <li><span className={styles.diIcon}>$</span> $99 / month</li>
                <li><span className={styles.diIcon}>✓</span> 600 minutes included</li>
                <li><span className={styles.diIcon}>$</span> $0.16 / min</li>
              </ul>
              <hr className={styles.priceDivider} />
              <ul className={styles.priceFeatures}>
                <li><span className={styles.ck}>✓</span> 3 concurrent calls</li>
                <li><span className={styles.ck}>✓</span> 1 phone number</li>
                <li><span className={styles.ck}>✓</span> Basic analytics dashboard</li>
                <li><span className={styles.ck}>✓</span> Email support</li>
              </ul>
            </div>

            {/* Professional */}
            <div className={styles.priceCard}>
              <div className={styles.planTitle}>Professional</div>
              <div className={styles.planDesc}>More capacity, deeper integrations, and 24/7 call handling with zero hold times.</div>
              <Link href="/register" className={styles.btnPricePrimary}>Get Started</Link>
              <div className={styles.priceDetailsLabel}>Details</div>
              <ul className={styles.priceDetailsItems}>
                <li><span className={styles.diIcon}>$</span> $299 / month</li>
                <li><span className={styles.diIcon}>✓</span> 2,000 minutes included</li>
                <li><span className={styles.diIcon}>$</span> $0.15 / min</li>
              </ul>
              <hr className={styles.priceDivider} />
              <ul className={styles.priceFeatures}>
                <li><span className={styles.ck}>✓</span> 10 concurrent calls</li>
                <li><span className={styles.ck}>✓</span> 3 phone numbers</li>
                <li><span className={styles.ck}>✓</span> Advanced analytics</li>
                <li><span className={styles.ck}>✓</span> Priority support</li>
                <li><span className={styles.ck}>✓</span> Custom voice & personality</li>
                <li><span className={styles.ck}>✓</span> 15+ healthcare integrations & growing</li>
                <li><span className={styles.ck}>✓</span> HIPAA / BAA Included</li>
              </ul>
            </div>

            {/* Enterprise */}
            <div className={styles.priceCard}>
              <div className={styles.planTitle}>Enterprise</div>
              <div className={styles.planDesc}>Never miss a call. Enterprise-grade AI voice agents running 24/7 at full scale.</div>
              <Link href="/register" className={styles.btnPriceOutline}>Get Started</Link>
              <div className={styles.priceDetailsLabel}>Details</div>
              <ul className={styles.priceDetailsItems}>
                <li><span className={styles.diIcon}>$</span> $699 / month</li>
                <li><span className={styles.diIcon}>✓</span> 6,500 minutes included</li>
                <li><span className={styles.diIcon}>$</span> $0.10 / min</li>
              </ul>
              <hr className={styles.priceDivider} />
              <ul className={styles.priceFeatures}>
                <li><span className={styles.ck}>✓</span> 30 concurrent calls</li>
                <li><span className={styles.ck}>✓</span> 10 phone numbers</li>
                <li><span className={styles.ck}>✓</span> Dedicated account manager</li>
                <li><span className={styles.ck}>✓</span> 15+ healthcare integrations + custom API support</li>
                <li><span className={styles.ck}>✓</span> SLA guarantee</li>
                <li><span className={styles.ck}>✓</span> HIPAA / BAA Included</li>
              </ul>
            </div>
          </div>

          {/* Overage note */}
          <p className={styles.overageNote}>
            All plans are HIPAA compliant · Overage minutes billed at $0.30/min
          </p>
          <p className={styles.overageNote}>
            <strong>Data &amp; API fees</strong> — Billed separately by the provider, not by NovaMind AI
          </p>

          {/* Custom — full width banner */}
          <div className={styles.priceCardCustom}>
            <div className={styles.priceCardCustomLeft}>
              <div className={styles.planTitle}>Custom</div>
              <div className={styles.planDesc}>For hospital systems, white-label resellers, and strategic accounts that need a fully tailored solution. Custom integrations, custom SLA, and dedicated build-out — on your terms.</div>
              <ul className={styles.priceFeatures}>
                <li><span className={styles.ck}>✓</span> Unlimited minutes</li>
                <li><span className={styles.ck}>✓</span> Custom concurrent calls</li>
                <li><span className={styles.ck}>✓</span> Custom phone numbers</li>
                <li><span className={styles.ck}>✓</span> Integrate with any platform — no limits</li>
                <li><span className={styles.ck}>✓</span> White-label & reseller options</li>
                <li><span className={styles.ck}>✓</span> Available for any industry — fully tailored to your needs</li>
                <li><span className={styles.ck}>✓</span> HIPAA / BAA Included</li>
              </ul>
            </div>
            <div className={styles.priceCardCustomRight}>
              <div className={styles.priceCardCustomPrice}>Let's Talk</div>
              <p className={styles.priceCardCustomNote}>Custom pricing tailored to your needs</p>
              <Link href="/contact" className={styles.btnPriceOutline}>Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionSpacer} />

      {/* ── FAQ ── */}
      <section id="faq" className={styles.faqSection}>
        <div className={styles.sectionInner}>
          <div className={styles.faqSectionHeader}>
            <span className={styles.faqEyebrow}>FAQ</span>
            <h2 className={styles.faqH2}>Common Questions</h2>
          </div>
          <div className={styles.faqList}>
            {faqItems.map((item, i) => (
              <div key={i} className={cx(styles.faqItem, openFaq === i && styles.open)}>
                <button className={styles.faqBtn} onClick={() => toggleFaq(i)}>
                  {item.q}
                  <span className={styles.faqIcon}><WaveIcon /></span>
                </button>
                {openFaq === i && (
                  <div className={styles.faqBody}>
                    {item.a.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < item.a.split('\n').length - 1 && <br />}</span>
                    ))}
                    {'link' in item && item.link && (
                      <span><br /><Link href={item.link.href} className={styles.faqLink}>{item.link.text} →</Link></span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <div className={styles.ctaSection}>
        <div className={styles.ctaCard}>
          <h2>Never Miss a Call.<br />Never Lose a Customer.</h2>
          <p>Consistent, professional service that turns every interaction into an opportunity.</p>
          <div className={styles.ctaBtns}>
            <Link href="/register" className={styles.btnPrimary}>Get Started Now</Link>
            <button className={styles.btnCtaGhost}>Talk to Sales</button>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <Footer />
    </div>
  );
}
